import { Component, ErrorInfo, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import { trpc } from './lib/trpc'
import App from './App'
import './index.css'

class ErrorBoundary extends Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('App crashed:', error, info) }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error
      return (
        <div style={{ background: '#0A0A0A', color: '#f87171', padding: 32, fontFamily: 'monospace', minHeight: '100vh' }}>
          <h2 style={{ marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ fontSize: 14, color: '#ccc' }}>An unexpected error occurred. Please refresh the page or contact support.</p>
          {import.meta.env.DEV && <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginTop: 16 }}>{err.message}{'\n\n'}{err.stack}</pre>}
        </div>
      )
    }
    return this.props.children
  }
}

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY — auth will not work')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

function AppWithAuth() {
  const { getToken } = useAuth()

  const trpcClient = useMemo(() => trpc.createClient({
    links: [
      httpBatchLink({
        url: `${import.meta.env.VITE_API_URL ?? ''}/api/trpc`,
        async headers() {
          try {
            const token = await Promise.race([
              getToken(),
              new Promise<null>((resolve) => setTimeout(() => resolve(null), 500)),
            ])
            return token ? { Authorization: `Bearer ${token}` } : {}
          } catch {
            return {}
          }
        },
      }),
    ],
  }), [getToken])

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <BrowserRouter>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <AppWithAuth />
      </ClerkProvider>
    </BrowserRouter>
  </ErrorBoundary>
)
